import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Film, Play } from 'lucide-react'
import { videos } from '../api/client'
import VideoMenu from '../components/VideoMenu'

function formatDuration(ms: number | null): string {
  if (!ms) return '--:--'
  const minutes = Math.floor(ms / 1000 / 60)
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (hours > 0) {
    return `${hours}h ${mins}m`
  }
  return `${mins}m`
}

export default function Movies() {
  const [watchedStatus, setWatchedStatus] = useState<Record<string, boolean>>({})

  const { data: movieList, isLoading } = useQuery({
    queryKey: ['movies'],
    queryFn: () => videos.listMovies(100, 0),
  })

  // Fetch watched status for all movies
  useEffect(() => {
    if (movieList && movieList.length > 0) {
      const ids = movieList.map(m => m.id)
      videos.getWatchedBatch(ids).then(setWatchedStatus)
    }
  }, [movieList])

  const handleWatchedChange = (videoId: string, watched: boolean) => {
    setWatchedStatus(prev => ({ ...prev, [videoId]: watched }))
  }

  return (
    <div className="p-4 sm:p-6">
      <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Movies</h1>

      {isLoading ? (
        <div className="text-dark-400">Loading...</div>
      ) : movieList && movieList.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-6">
          {movieList.map((movie) => {
            const isWatched = watchedStatus[movie.id] || false
            return (
              <Link
                key={movie.id}
                to={`/movies/${movie.id}`}
                className="card group hover:bg-dark-700 transition-colors cursor-pointer relative block"
              >
                <div className="aspect-[2/3] mb-4 bg-dark-700 rounded-lg flex items-center justify-center overflow-hidden relative">
                  {movie.thumbnail_path ? (
                    <img
                      src={movie.thumbnail_path}
                      alt={movie.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <Film size={48} className="text-dark-500" />
                  )}

                  {/* Unwatched indicator - corner triangle */}
                  {!isWatched && (
                    <div className="absolute top-0 right-0 w-0 h-0 border-t-[28px] border-t-primary-500 border-l-[28px] border-l-transparent" />
                  )}

                  {/* Hover overlay with play button */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Play size={48} className="text-white" />
                  </div>

                  {/* 3-dot menu */}
                  <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <VideoMenu
                      videoId={movie.id}
                      isWatched={isWatched}
                      onWatchedChange={(watched) => handleWatchedChange(movie.id, watched)}
                    />
                  </div>
                </div>
                <h3 className="font-medium truncate group-hover:text-primary-400">
                  {movie.title}
                </h3>
                <div className="flex items-center gap-2 text-sm text-dark-400">
                  {movie.year && <span>{movie.year}</span>}
                  {movie.duration_ms && (
                    <>
                      <span>•</span>
                      <span>{formatDuration(movie.duration_ms)}</span>
                    </>
                  )}
                </div>
                {movie.resolution && (
                  <span className="text-xs text-dark-500">{movie.resolution}</span>
                )}
              </Link>
            )
          })}
        </div>
      ) : (
        <div className="card text-center py-12">
          <Film size={48} className="text-dark-500 mx-auto mb-4" />
          <p className="text-dark-400">No movies found</p>
          <p className="text-dark-500 text-sm mt-2">
            Add a movies library and scan it to see your movies here
          </p>
        </div>
      )}
    </div>
  )
}
